import type { IngestBatch } from "@tracking-web/shared";
import { uid } from "./util.js";
import { createQueue, type StorageLike } from "./queue.js";

/**
 * Transporte: bufferiza eventos y los entrega por lotes con una COLA persistente
 * (Bloque B2) que sobrevive a fallos de red y al cierre de página — clave con
 * tráfico móvil (redes inestables, backgrounding/bfcache).
 *
 * Dos contextos de flush (lo gestiona queue.ts):
 *  - Activo (cada N ms / buffer lleno): `fetch` observable → si falla, persiste y
 *    reintenta con backoff. En el camino feliz NO toca localStorage.
 *  - Cierre (lo dispara index.ts en visibilitychange→hidden / pagehide): write-ahead
 *    (persistir ANTES) + sendBeacon, porque el beacon no confirma entrega y no da
 *    tiempo a esperar un fetch. Se drena en la siguiente carga; el servidor deduplica
 *    por eventId (B1).
 */

type TransportOpts = {
  origin: string;
  siteKey: string;
  sessionId: string;
  anonId: string;
  meta: NonNullable<IngestBatch["m"]>;
  sampleRate: number;
  maxBatch?: number;
  flushIntervalMs?: number;
};

/** Adaptador de localStorage con try/catch. Devuelve null si no se puede escribir
 *  (iOS Safari modo privado lanza en setItem) → la cola degrada a memoria. */
function makeStorage(): StorageLike | null {
  try {
    const probe = "__tw_probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
  } catch {
    return null; // sin persistencia: la cola opera solo en memoria, nunca rompe
  }
  return {
    getItem: (k) => {
      try { return localStorage.getItem(k); } catch { return null; }
    },
    setItem: (k, v) => localStorage.setItem(k, v), // puede lanzar (cuota) → la cola lo captura
    removeItem: (k) => {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
    },
    keys: () => {
      try { return Object.keys(localStorage); } catch { return []; }
    },
  };
}

// Eventos siempre relevantes que NO se deben muestrear: loop de personalización y
// TODO lo de experimentos (si se muestrean, exposiciones/conversiones se descartan
// por separado → la tasa del test se corrompe). bandit_ tampoco: muestrear las
// impresiones del bandit corrompería la recompensa (IPVPS) y la propensity logueada.
// page_exit/active_time tampoco: el tiempo es métrica de dinero. ad_impression tampoco:
// el IPVPS es el RECUENTO de esas filas; muestrearlas corrompería el revenue medido.
// section_time tampoco: el mapa de atención por URL es la SUMA de esos tramos;
// muestrearlos sesgaría el tiempo por franja.
// Exportado para test directo de la invariante (ver transport.test.ts + CLAUDE.md).
export const NEVER_SAMPLE = /^(session_|adaptation_|rule_matched$|experiment_|bandit_|page_exit$|active_time$|ad_impression$|section_time$)/;

/** True si el evento NUNCA debe muestrearse (métrica de dinero / loop de personalización). */
export function isNeverSampled(type: string): boolean {
  return NEVER_SAMPLE.test(type);
}

export function createTransport(opts: TransportOpts) {
  const maxBatch = opts.maxBatch ?? 20;
  const flushIntervalMs = opts.flushIntervalMs ?? 5000;
  const url = `${opts.origin}/api/ingest`;

  // Petición CORS "simple" (text/plain → sin preflight; el servidor parsea JSON igual).
  const postJson = (body: string): Promise<boolean> =>
    fetch(url, {
      method: "POST",
      body,
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      keepalive: true,
      mode: "cors",
      credentials: "omit",
    })
      // Reintentar SOLO en 5xx o fallo de red. Un 4xx (p.ej. bad_batch) no se arregla
      // reintentando → se considera "hecho" y se descarta (no bucle infinito).
      .then((r) => r.status < 500)
      .catch(() => false);

  const beacon = (body: string): boolean => {
    try {
      return (
        typeof navigator !== "undefined" &&
        !!navigator.sendBeacon &&
        navigator.sendBeacon(url, new Blob([body], { type: "text/plain;charset=UTF-8" }))
      );
    } catch {
      return false;
    }
  };

  const queue = createQueue({
    envelope: { k: opts.siteKey, s: opts.sessionId, a: opts.anonId, m: opts.meta },
    storage: makeStorage(),
    postJson,
    beacon,
    now: () => Date.now(),
  });

  let pending = 0;
  function flushActive() {
    pending = 0;
    void queue.flushActive();
  }

  function track(type: string, props?: Record<string, unknown>, urlOverride?: string) {
    if (opts.sampleRate < 1 && !isNeverSampled(type) && Math.random() > opts.sampleRate) {
      return;
    }
    // eventId (i) único por evento, asignado UNA vez aquí: el servidor deduplica por
    // él (skipDuplicates), lo que hace seguros los reintentos de la cola. NUNCA se
    // regenera al reenviar (la cola persiste el evento con su id).
    //
    // `u` = ruta de la página AHORA mismo. La url del envelope (`m.url`) se congela en el
    // boot, así que en publishers SPA (Next/React) atribuiría todos los eventos a la 1ª
    // página; sellar por-evento aquí lo arregla en SPA y MPA por igual. Es un getter
    // síncrono barato (no provoca reflow) y se serializa con el evento → sobrevive a la
    // cola persistente. pathname (no href) para no inflar el payload.
    // `urlOverride`: cuando el llamante ya sabe la URL correcta y location.pathname NO la
    // refleja (tramos de tiempo-por-sección de la página saliente en una navegación SPA,
    // donde el router avisa tras cambiar location), gana el override.
    const u = urlOverride ?? (typeof location !== "undefined" ? location.pathname : undefined);
    queue.add({ t: type, ts: Date.now(), p: props, i: uid(), u });
    if (++pending >= maxBatch) flushActive();
  }

  setInterval(flushActive, flushIntervalMs);
  // Recuperar conexión → reintentar lo persistido. El cierre (visibilitychange/
  // pagehide) lo dispara index.ts vía flushOnClose() para no duplicar listeners.
  window.addEventListener("online", () => void queue.drain(), { passive: true });
  // Al arrancar, drenar lo que quedó de una sesión anterior (no bloquea el arranque).
  void queue.drain();

  return { track, flush: flushActive, flushOnClose: queue.flushOnClose };
}
